import { Component, ElementRef, NgZone } from '@angular/core';
import { HeroMotifBase, RGB } from '../hero-motif/hero-motif.base';

const LOOP = 40;
const W0 = Math.PI * 2 / LOOP;
/** The sun's rays, the sun at the left (owner): a point h metres up casts its shadow this far across (toward the land) and along, per metre. */
const SUNX = 0.62;
/** Where the sun stands across the stage, in stage heights from its left edge. */
const SUN_U = 0.5;
const SUNZ = 0.4;

/** A macaw flying over the beach. */
interface Macaw {
  x: number;
  y: number;
  base: number;
  dir: 1 | -1;
  speed: number;
  size: number;
  bob: number;
  bobRate: number;
  phase: number;
  beat: number;
  rate: number;
  tint: number;
  rank: number;
  shown: number;
}

/** A sailboat out on the ocean, in metres: across (x, negative = out to sea), along the beach (z). */
interface Boat {
  x: number;
  z: number;
  speed: number;
  len: number;
}

/** A car on the avenue, in metres: its distance along it, its lane, its speed (+ away, - toward us). */
interface Car {
  z: number;
  lane: number;
  speed: number;
  col: RGB;
}


/**
 * Rio de Janeiro's hero board (2026-10-17, redrawn from scratch): Copacabana seen from its sand, in true
 * perspective (a camera 10 m up, looking along the beach; every object placed in metres and projected).
 * At the left the Atlantic to the horizon, its breakers rolling in along the shore; the wide beach curving
 * away to Leme with its umbrellas and sunbeds; the black and white wave mosaic of the promenade, the palms,
 * the avenue and the row of apartment blocks facing the sea; green hills behind them, and far off at the
 * end of the curve the Sugarloaf. Dock Town's macaws fly over; the pointer, a finger or the counter
 * counting bring them out in numbers. The page's wallpaper is the macaws alone. Sized by the stage's
 * height.
 */
@Component( {
  selector: 'app-hero-rio-de-janeiro',
  template: '<canvas #canvas class="hrio-canvas" aria-hidden="true"></canvas>',
  styles: [ ':host { display: block; position: absolute; inset: 0; overflow: hidden; pointer-events: none; }',
    '.hrio-canvas { display: block; position: absolute; inset: 0; }' ],
} )
export class HeroRioDeJaneiroComponent extends HeroMotifBase {

  private back: HTMLCanvasElement | null = null;
  private clouds: HTMLCanvasElement | null = null;
  /** The palms, on a layer of their own so the wind can stir them. */
  private palms: HTMLCanvasElement | null = null;
  /** The Sugarloaf's cable car line on screen, a quadratic: start, sag, end. */
  private cable: [ number, number, number, number, number, number ] | null = null;
  private cloudOff = 0;
  private macaws: Macaw[] = [];
  private boats: Boat[] = [];
  private cars: Car[] = [];
  private scaleK = 1;
  // the camera: eye height (m), focal length and horizon (px), the vanishing point's x (px)
  private eye = 10;
  private F = 0;
  private hz = 0;
  private vx = 0;

  constructor( zone: NgZone, host: ElementRef<HTMLElement> ) {
    super( zone, host );
    this.loop = LOOP;
    this.ease = 1.2;
  }

  protected colorsChanged(): void {
    this.back = null;
  }

  // ---- the world, in metres ----

  /** Project a point: x across (negative = out to sea), y up, z along the beach away from us. */
  private P( x: number, y: number, z: number ): [ number, number ] {
    return [ this.vx + this.F * x / z, this.hz - this.F * ( y - this.eye ) / z ];
  }

  /** The water's edge: the beach curving gently toward the land as it runs away to Leme. */
  private shoreX( z: number ): number {
    // the water near enough that the ocean and its surf fill the lower left, not bare sand
    return -28 + 0.00004 * z * z;
  }

  /** The promenade's sea edge, the beach being 85 m wide. */
  private promX( z: number ): number {
    return this.shoreX( z ) + 85;
  }

  /** The buildings' fronts, behind the promenade (9 m), the avenue (24 m) and the pavement (5 m). */
  private frontX( z: number ): number {
    return this.promX( z ) + 38;
  }

  protected build(): void {
    const W = this.width;
    const H = this.height;
    const k = this.scaleK = Math.max( 0.55, Math.min( 1.25, H / 760 ) );
    this.F = 0.9 * H;
    this.hz = 0.5 * H;
    // the vanishing point right of the hero's words, so the busy far end of the beach sits beside them
    // and the calm ocean behind them
    this.vx = Math.min( W / 2 + 0.44 * H, W * 0.8 );
    this.back = null;
    const r = this.seededLocal( 23 );
    this.macaws = [];
    for ( let i = 0; i < 26; i++ ) {
      const far = r();
      this.macaws.push( {
        x: r() * W, y: 0, base: H * ( 0.05 + 0.35 * r() ), dir: r() < 0.5 ? 1 : -1,
        speed: ( 55 + r() * 40 ) * k * ( 1 - 0.4 * far ), size: ( 24 - 10 * far ) * k,
        bob: ( 6 + r() * 14 ) * k, bobRate: 0.3 + r() * 0.3, phase: r() * Math.PI * 2,
        beat: r() * 10, rate: 3.6 + r() * 1.2, tint: Math.floor( r() * 2 ),
        rank: i === 0 ? 0 : i === 1 ? 0.05 : r(), shown: 0,
      } );
    }
    // sailboats well out beyond the breakers, a few near, more far off
    this.boats = [];
    for ( let i = 0; i < 10; i++ ) {
      const z = 250 + Math.pow( r(), 1.4 ) * 3200;
      this.boats.push( { x: -260 - r() * 1400 - z * 0.15, z, speed: 0.6 + r() * 1.4, len: 9 + r() * 6 } );
    }
    // the traffic on the avenue: away from us on the far carriageway, toward us on the near one
    this.cars = [];
    for ( let z = 20; z < 2600; z += 10 + r() * 30 ) {
      const lane = [ 11.5, 17.5, 24.5, 30.5 ][ Math.floor( r() * 4 ) ];
      const q = r();
      const col: RGB = q < 0.35 ? [ 244, 244, 240 ] : q < 0.6 ? [ 170, 176, 182 ] : q < 0.8 ? [ 44, 46, 52 ] : q < 0.9 ? [ 236, 190, 40 ] : [ 196, 58, 52 ];
      this.cars.push( { z, lane, speed: ( lane > 20 ? 1 : -1 ) * ( 11 + r() * 6 ), col } );
    }
  }

  protected paint( ctx: CanvasRenderingContext2D, dt: number ): void {
    const W = this.width;
    const H = this.height;
    if ( this.frozen ) {
      // the page's wallpaper: the macaws alone, still, all down the page
      this.drawWallMacaws( ctx );
      return;
    }
    if ( !this.back ) { this.bake( ctx.getTransform().a ); }
    if ( !this.back ) { return; }
    // the sky, the hills, the ocean, the beach and the city, all still
    ctx.drawImage( this.back, 0, 0, W, H );
    if ( this.clouds ) {
      if ( dt > 0 ) { this.cloudOff = ( this.cloudOff + dt * 6 * this.scaleK ) % W; }
      const off = Math.round( this.cloudOff );
      ctx.drawImage( this.clouds, off, 0, W, H );
      ctx.drawImage( this.clouds, off - W, 0, W, H );
    }
    this.drawCableCars( ctx );
    this.drawCars( ctx, dt );
    this.drawBoats( ctx, dt );
    this.drawSurf( ctx );
    this.drawPalms( ctx );
    this.drawMacaws( ctx, dt );
  }

  /**
   * The palms stirred by the wind (owner: as the forest swayed before): the layer drawn in thin level
   * strips, each pushed sideways by a slow wave, the crowns high up moving most and the feet not at all,
   * gusting now and then; on the seamless loop.
   */
  private drawPalms( ctx: CanvasRenderingContext2D ): void {
    const layer = this.palms;
    if ( !layer ) { return; }
    const W = this.width;
    const H = this.height;
    const sc = layer.width / W;
    const t = this.time * W0;
    const gust = 0.75 + 0.25 * Math.sin( t * 2 ) + 0.2 * Math.sin( t * 5 + 1 );
    const step = 2;
    for ( let y = 0; y < H; y += step ) {
      const up = Math.max( 0, Math.min( 1, ( this.hz + 0.3 * H - y ) / ( 0.45 * H ) ) );
      const off = 2.4 * this.scaleK * gust * Math.pow( up, 1.4 ) * ( Math.sin( y * 0.03 - t * 7 ) * 0.7 + Math.sin( y * 0.09 - t * 12 ) * 0.3 );
      ctx.drawImage( layer, 0, y * sc, layer.width, step * sc, off, y, W, step );
    }
  }

  /**
   * The cars driving along the avenue (owner), boxes in perspective: from 10 m up we see the roof, the side
   * toward the sea and the back; a lower body and a narrower glazed cabin, the wheels, the lights; specks
   * far off. Each wraps round from one end of the avenue to the other, unseen.
   */
  private drawCars( ctx: CanvasRenderingContext2D, dt: number ): void {
    const F = this.F;
    const P = ( x: number, y: number, z: number ) => this.P( x, y, z );
    const [ mint, , , , yellow ] = this.five();
    const haze: RGB = this.mix( this.mix( yellow, [ 255, 255, 255 ], 0.6 ), mint, 0.3 );
    const poly = ( pts: [ number, number ][], fill: string ) => {
      ctx.fillStyle = fill;
      ctx.beginPath();
      pts.forEach( ( [ x, y ], i ) => i ? ctx.lineTo( x, y ) : ctx.moveTo( x, y ) );
      ctx.closePath();
      ctx.fill();
    };
    for ( const c of this.cars ) {
      if ( dt > 0 ) {
        c.z += c.speed * dt * 0.6;
        if ( c.z > 2600 ) { c.z = 20; } else if ( c.z < 20 ) { c.z = 2600; }
      }
    }
    const sorted = this.cars.slice().sort( ( a, b ) => b.z - a.z );
    for ( const c of sorted ) {
      const z = c.z;
      const col = this.mix( c.col, haze, ( 1 - Math.exp( -z / 3500 ) ) * 0.6 );
      const x = this.promX( z ) + c.lane;
      const s = F / z;
      if ( s < 1.2 ) {
        const [ px, py ] = P( x, 0.7, z );
        ctx.fillStyle = this.rgba( col, 0.9 );
        ctx.fillRect( px - 0.7, py - 0.4, 1.4, 0.8 );
        continue;
      }
      // (owner: on the widest screens the cars stretched) past about 39 degrees off the axis a car keeps its
      // true place and its line along the lanes, but its length on screen is drawn back toward what it is
      // nearer the middle, so at the frame's edge it keeps its real shape
      const zm = z + 2.25, k = Math.min( 1, 0.8 * zm / Math.max( 1e-3, Math.abs( x ) ) );
      const [ cx, cy ] = P( x, 0.7, zm ), [ ax, ay ] = P( x, 0.7, zm + 1 ), al = Math.hypot( ax - cx, ay - cy ) || 1;
      const dx = ( ax - cx ) / al, dy = ( ay - cy ) / al;
      const Pc = ( px: number, py: number, pz: number ): [ number, number ] => {
        const [ sx, sy ] = P( px, py, pz ), along = ( sx - cx ) * dx + ( sy - cy ) * dy;
        return [ sx - dx * along * ( 1 - k ), sy - dy * along * ( 1 - k ) ];
      };
      // a real car's shape (owner): 4.5 m long, 1.8 m wide; a body to 0.85 m with the bonnet and the boot
      // on it, a cabin to 1.45 m whose windscreen and rear window slope, its side windows between pillars
      const L2 = 4.5, Wd = 1.8, hb = 0.85, hc = 1.45;
      const x0 = x - Wd / 2, x1 = x + Wd / 2;
      // the cabin's foot and roof along the car: the rear window from rb0 up to rr0, the roof to rr1, the
      // windscreen down to rb1 (for a car going away the back is toward us)
      const back = c.speed > 0;
      const rb0 = z + ( back ? 0.9 : 1.0 ), rr0 = z + ( back ? 1.5 : 1.3 ), rr1 = z + ( back ? 3.1 : 3.0 ), rb1 = z + ( back ? 3.6 : 3.7 );
      const glass: RGB = [ 46, 60, 72 ], glassLit: RGB = [ 120, 146, 166 ];
      const body = this.mix( col, [ 255, 255, 255 ], 0.06 ), bodySide = this.mix( col, [ 0, 0, 0 ], 0.1 ), bodyRear = this.mix( col, [ 0, 0, 0 ], 0.25 ), top = this.mix( col, [ 255, 255, 255 ], 0.16 );
      // its shadow on the road, cast to the right by the sun at the left
      poly( [ Pc( x0, 0, z ), Pc( x1 + 0.9, 0, z ), Pc( x1 + 0.9, 0, z + L2 + 0.4 ), Pc( x0, 0, z + L2 + 0.4 ) ], 'rgba(30,30,30,0.25)' );
      // the wheels, under the body's side
      if ( s > 2.5 ) {
        for ( const wz of [ z + 0.85, z + L2 - 0.85 ] ) {
          poly( [ Pc( x0 + 0.05, 0, wz - 0.32 ), Pc( x0 + 0.05, 0.64, wz - 0.32 ), Pc( x0 + 0.05, 0.64, wz + 0.32 ), Pc( x0 + 0.05, 0, wz + 0.32 ) ], 'rgb(28,28,30)' );
        }
      }
      // the body: its side toward the sea, its end toward us, its bonnet, boot and the floor of the cabin
      poly( [ Pc( x0, 0.3, z ), Pc( x0, hb, z + 0.15 ), Pc( x0, hb, z + L2 - 0.15 ), Pc( x0, 0.3, z + L2 ) ], this.rgba( bodySide, 1 ) );
      poly( [ Pc( x0, 0.3, z ), Pc( x1, 0.3, z ), Pc( x1, hb, z + 0.15 ), Pc( x0, hb, z + 0.15 ) ], this.rgba( bodyRear, 1 ) );
      poly( [ Pc( x0, hb, z + 0.15 ), Pc( x1, hb, z + 0.15 ), Pc( x1, hb, z + L2 - 0.15 ), Pc( x0, hb, z + L2 - 0.15 ) ], this.rgba( body, 1 ) );
      // the cabin: the sloping glass toward us, the side windows, the roof
      const cx0 = x0 + 0.1, cx1 = x1 - 0.1;
      poly( [ Pc( cx0, hb, rb0 ), Pc( cx1, hb, rb0 ), Pc( cx1, hc, rr0 ), Pc( cx0, hc, rr0 ) ], this.rgba( glassLit, 1 ) );
      poly( [ Pc( cx0, hb, rb0 ), Pc( cx0, hc, rr0 ), Pc( cx0, hc, rr1 ), Pc( cx0, hb, rb1 ) ], this.rgba( glass, 1 ) );
      poly( [ Pc( cx0, hc, rr0 ), Pc( cx1, hc, rr0 ), Pc( cx1, hc, rr1 ), Pc( cx0, hc, rr1 ) ], this.rgba( top, 1 ) );
      if ( s > 3.5 ) {
        // the pillars between the side windows, the sill under them
        const pill = this.rgba( this.mix( col, [ 0, 0, 0 ], 0.2 ), 1 );
        const midZ = ( rr0 + rr1 ) / 2;
        poly( [ Pc( cx0, hb, midZ - 0.08 ), Pc( cx0, hc, midZ - 0.08 ), Pc( cx0, hc, midZ + 0.08 ), Pc( cx0, hb, midZ + 0.08 ) ], pill );
        poly( [ Pc( cx0, hb, rb0 ), Pc( cx0, hb + 0.06, rb0 ), Pc( cx0, hb + 0.06, rb1 ), Pc( cx0, hb, rb1 ) ], pill );
        // the lights at the end toward us: red behind, white in front
        for ( const lx of [ x0 + 0.1, x1 - 0.45 ] ) {
          poly( [ Pc( lx, 0.55, z + 0.05 ), Pc( lx + 0.35, 0.55, z + 0.05 ), Pc( lx + 0.35, 0.72, z + 0.1 ), Pc( lx, 0.72, z + 0.1 ) ], back ? 'rgb(214,40,40)' : 'rgb(250,246,220)' );
        }
        poly( [ Pc( x0, 0.3, z - 0.04 ), Pc( x1, 0.3, z - 0.04 ), Pc( x1, 0.42, z - 0.04 ), Pc( x0, 0.42, z - 0.04 ) ], 'rgba(30,30,32,0.8)' );
      }
    }
  }

  /** The Sugarloaf's two cable cars, one going up as the other comes down, hanging from the line. */
  private drawCableCars( ctx: CanvasRenderingContext2D ): void {
    if ( !this.cable ) { return; }
    const [ ax, ay, mx, my, bx, by ] = this.cable;
    const t = this.time * W0 * 2;
    const s = Math.max( 2.4, this.height * 0.006 );
    for ( const u of [ 0.06 + 0.88 * ( 0.5 - 0.5 * Math.cos( t ) ), 0.06 + 0.88 * ( 0.5 + 0.5 * Math.cos( t ) ) ] ) {
      const v = 1 - u;
      const x = v * v * ax + 2 * v * u * mx + u * u * bx, y = v * v * ay + 2 * v * u * my + u * u * by;
      ctx.strokeStyle = 'rgba(70,76,80,0.8)';
      ctx.lineWidth = 0.6;
      ctx.beginPath(); ctx.moveTo( x, y ); ctx.lineTo( x, y + s * 0.6 ); ctx.stroke();
      ctx.fillStyle = 'rgb(240,240,236)';
      ctx.fillRect( x - s * 0.6, y + s * 0.6, s * 1.2, s * 0.85 );
      ctx.fillStyle = 'rgba(70,120,150,0.85)';
      ctx.fillRect( x - s * 0.5, y + s * 0.72, s, s * 0.35 );
    }
  }

  /** The sailboats drifting slowly along the coast, their size and place by their distance. */
  private drawBoats( ctx: CanvasRenderingContext2D, dt: number ): void {
    const sorted = this.boats.slice().sort( ( a, b ) => b.z - a.z );
    for ( const b of sorted ) {
      if ( dt > 0 ) { b.z += b.speed * dt * 3; if ( b.z > 3600 ) { b.z = 250; } }
      const [ x, y ] = this.P( b.x, 0, b.z );
      const s = this.F * b.len / b.z;
      if ( x < -s || x > this.width + s ) { continue; }
      ctx.fillStyle = 'rgba(250,250,248,0.95)';
      ctx.fillRect( x - s * 0.5, y - s * 0.08, s, s * 0.08 );
      ctx.fillStyle = 'rgba(90,90,90,0.8)';
      ctx.fillRect( x - 0.3, y - s * 1.1, Math.max( 0.5, s * 0.02 ), s * 1.02 );
      ctx.fillStyle = 'rgb(250,250,248)';
      ctx.beginPath(); ctx.moveTo( x, y - s * 1.08 ); ctx.quadraticCurveTo( x - s * 0.22, y - s * 0.6, x - s * 0.36, y - s * 0.12 ); ctx.lineTo( x, y - s * 0.12 ); ctx.closePath(); ctx.fill();
      ctx.fillStyle = 'rgb(236,240,242)';
      ctx.beginPath(); ctx.moveTo( x + 0.5, y - s * 0.95 ); ctx.quadraticCurveTo( x + s * 0.18, y - s * 0.5, x + s * 0.34, y - s * 0.12 ); ctx.lineTo( x + 0.5, y - s * 0.12 ); ctx.closePath(); ctx.fill();
    }
  }

  /**
   * The surf, in perspective: lines of breakers parallel to the shore rolling in, each whiter and wider
   * as it nears the sand and breaking into foam, the wash running up the beach and sliding back.
   */
  private drawSurf( ctx: CanvasRenderingContext2D ): void {
    const t = this.time * W0;
    const zNear = this.zEnter() * 0.85, zFar = 3600;
    const zs: number[] = [];
    for ( let z = zNear; z < zFar; z *= 1.025 ) { zs.push( z ); }
    // a band on the water between two offsets from the water's edge (metres, toward the sea), wavering a
    // little along the shore: in perspective, so it is broad near us and a hairline far off
    const band = ( o0: number, o1: number, fill: string, wob: number ) => {
      ctx.fillStyle = fill;
      ctx.beginPath();
      zs.forEach( ( z, i ) => { const w = Math.sin( z * 0.021 + wob ) * 1.4 + Math.sin( z * 0.057 + wob * 2 ) * 0.6; const [ px, py ] = this.P( this.shoreX( z ) - o0 + w, 0, z ); i ? ctx.lineTo( px, py ) : ctx.moveTo( px, py ); } );
      for ( let i = zs.length - 1; i >= 0; i-- ) { const z = zs[ i ]; const w = Math.sin( z * 0.021 + wob ) * 1.4 + Math.sin( z * 0.057 + wob * 2 ) * 0.6; const [ px, py ] = this.P( this.shoreX( z ) - o1 + w, 0, z ); ctx.lineTo( px, py ); }
      ctx.closePath();
      ctx.fill();
    };
    // three breakers rolling in, a set four times a loop: the dark face of the wave, its white crest, and
    // once it breaks the foam spreading behind it
    for ( let i = 0; i < 3; i++ ) {
      const p = ( ( t * 6 / ( Math.PI * 2 ) + i / 3 ) % 1 + 1 ) % 1;
      const off = 48 * ( 1 - p ) + 3;
      const a = Math.sin( Math.PI * Math.min( 1, p * 1.1 ) );
      if ( a < 0.03 ) { continue; }
      const wob = i * 2 + t;
      band( off + 2.2, off, `rgba(10,90,110,${ 0.18 * a })`, wob );
      band( off, off - 0.6 - 1.2 * p, `rgba(255,255,255,${ 0.85 * a })`, wob );
      if ( p > 0.45 ) { band( off - 0.6 - 1.2 * p, off - 2 - 7 * ( p - 0.45 ), `rgba(245,250,250,${ 0.45 * a })`, wob + 0.5 ); }
    }
    // the wash: a thin sheet of water running up the sand and sliding back, its foamy edge
    const w = 0.5 + 0.5 * Math.sin( t * 6 );
    band( 3, -3 - 4 * w, `rgba(170,220,225,${ 0.35 + 0.2 * w })`, t * 3 );
    band( -2.6 - 4 * w, -3 - 4 * w, `rgba(255,255,255,${ 0.6 + 0.3 * w })`, t * 3 );
  }

  /** The distance where the water's edge comes into view at the left edge of the stage. */
  private zEnter(): number {
    for ( let z = 5; z < 4000; z *= 1.05 ) { if ( this.P( this.shoreX( z ), 0, z )[ 0 ] > -40 ) { return z; } }
    return 40;
  }

  /**
   * The macaws crossing the bay, beating all the time as macaws do, rising and falling a little; a few
   * at rest, many and quicker when the counter counts; called out somewhere and fading in, climbing
   * away as they go.
   */
  private drawMacaws( ctx: CanvasRenderingContext2D, dt: number ): void {
    const W = this.width;
    const e = this.energy;
    const share = 0.08 + 0.92 * e;
    for ( const m of this.macaws ) {
      const want = m.rank <= share ? 1 : 0;
      if ( dt > 0 && want && m.shown < 0.02 ) { m.x = Math.random() * W; }
      m.shown = dt > 0 ? m.shown + ( want - m.shown ) * Math.min( 1, dt * 0.55 ) : want;
      if ( dt > 0 ) {
        m.x += m.dir * m.speed * ( 1 + 0.6 * e ) * dt;
        m.phase += dt * m.bobRate;
        m.beat += dt * m.rate * ( 1 + 0.25 * e );
        const lim = m.size * 3;
        if ( m.x > W + lim ) { m.x = -lim; } else if ( m.x < -lim ) { m.x = W + lim; }
      }
      if ( m.shown < 0.02 ) { continue; }
      m.y = m.base + Math.sin( m.phase * 2 ) * m.bob;
      // the arm leads, the hand lags
      const theta = m.beat * Math.PI * 2 * 0.35;
      const arm = Math.sin( theta ) * 0.85;
      const hand = Math.sin( theta - 0.8 ) * 0.9;
      const away = 1 - m.shown;
      ctx.save();
      ctx.globalAlpha = Math.pow( m.shown, 0.7 );
      ctx.translate( m.x, m.y - away * m.bob * 2.5 );
      ctx.scale( ( 1 - 0.55 * away ) * m.dir, 1 - 0.55 * away );
      this.macaw( ctx, m.size, arm, hand, m.tint );
      ctx.restore();
    }
    ctx.globalAlpha = 1;
  }

  /**
   * One macaw facing right (y down), as Dock Town draws it: broad wings in two parts (the arm to the bend
   * at `arm`, the hand to the tip at `hand`; -1 raised, 0 out, 1 lowered), the flight feathers darker,
   * the long pointed tail of two feathers, the body, the head with its pale face, dark eye and hooked
   * bill. In the theme's colours: a blue one, a blue and yellow one, a green one with blue flight feathers.
   */
  private macaw( ctx: CanvasRenderingContext2D, s: number, arm: number, hand: number, tint: number ): void {
    const [ , , green, blue, yellow ] = this.five();
    const deep: RGB = this.mix( blue, [ 14, 40, 96 ], 0.45 );
    const tints: { body: RGB; wing: RGB; tip: RGB; tail: RGB }[] = [
      { body: blue, wing: this.mix( blue, deep, 0.15 ), tip: deep, tail: this.mix( blue, [ 255, 255, 255 ], 0.08 ) },
      { body: yellow, wing: blue, tip: deep, tail: this.mix( blue, deep, 0.2 ) },
      { body: green, wing: this.mix( green, [ 0, 60, 30 ], 0.15 ), tip: this.mix( blue, deep, 0.3 ), tail: this.mix( green, blue, 0.35 ) },
    ];
    const c = tints[ tint % tints.length ];
    const pose = ( a: number[], t: number ) => t <= 0 ? a[ 1 ] + ( a[ 0 ] - a[ 1 ] ) * -t : a[ 1 ] + ( a[ 2 ] - a[ 1 ] ) * t;
    const bendY = pose( [ -0.44, -0.26, 0.06 ], arm );
    const tipY = pose( [ -0.82, -0.14, 0.48 ], hand ), tipX = pose( [ 0.76, 0.94, 0.82 ], hand );
    const shade = Math.max( 0, -hand ) * 0.18;
    const dark = ( col: RGB, d: number ) => this.mix( col, [ 0, 0, 0 ], d );
    const fill = ( pts: number[][], col: RGB ) => {
      ctx.fillStyle = this.rgba( col, 1 );
      ctx.beginPath();
      pts.forEach( ( [ x, y ], i ) => i ? ctx.lineTo( x * s, y * s ) : ctx.moveTo( x * s, y * s ) );
      ctx.closePath();
      ctx.fill();
    };
    // the long tail behind, the far feather darker
    fill( [ [ -0.22, -0.02 ], [ -1.12, 0.12 ], [ -0.24, 0.1 ] ], dark( c.tail, 0.2 ) );
    fill( [ [ -0.22, 0 ], [ -1.02, 0.2 ], [ -0.2, 0.12 ] ], c.tail );
    for ( const side of [ -1, 1 ] ) {
      const sh = [ side * 0.1, -0.06 ], bk = [ side * 0.08, 0.12 ];
      const bend = [ side * 0.44, bendY ], end = [ side * tipX, tipY ], trail = [ side * 0.5, bendY + 0.24 ];
      fill( [ sh, bend, end, trail, bk ], dark( c.wing, shade ) );
      const a = [ bend[ 0 ] + ( end[ 0 ] - bend[ 0 ] ) * 0.45, bend[ 1 ] + ( end[ 1 ] - bend[ 1 ] ) * 0.45 ];
      const b = [ trail[ 0 ] + ( end[ 0 ] - trail[ 0 ] ) * 0.45, trail[ 1 ] + ( end[ 1 ] - trail[ 1 ] ) * 0.45 ];
      fill( [ a, end, b ], dark( c.tip, shade ) );
    }
    ctx.fillStyle = this.rgba( c.body, 1 );
    ctx.beginPath();
    ctx.ellipse( 0, 0.03 * s, 0.36 * s, 0.15 * s, 0, 0, Math.PI * 2 );
    ctx.fill();
    // the head: a blue and yellow macaw's crown is green-blue, the others' their own colour
    ctx.fillStyle = this.rgba( tint === 1 ? this.mix( blue, green, 0.4 ) : c.body, 1 );
    ctx.beginPath();
    ctx.arc( 0.36 * s, -0.05 * s, 0.16 * s, 0, Math.PI * 2 );
    ctx.fill();
    ctx.fillStyle = 'rgb(244,239,230)';
    ctx.beginPath();
    ctx.ellipse( 0.42 * s, -0.05 * s, 0.08 * s, 0.07 * s, 0, 0, Math.PI * 2 );
    ctx.fill();
    ctx.fillStyle = 'rgb(26,26,26)';
    ctx.beginPath();
    ctx.arc( 0.42 * s, -0.07 * s, 0.03 * s, 0, Math.PI * 2 );
    ctx.fill();
    fill( [ [ 0.48, -0.1 ], [ 0.6, -0.04 ], [ 0.56, 0.06 ], [ 0.48, 0.02 ] ], [ 46, 42, 40 ] );
  }

  /** The wallpaper: the macaws scattered still down the page, each caught at its own point in the beat. */
  private drawWallMacaws( ctx: CanvasRenderingContext2D ): void {
    const W = this.width;
    const H = this.height;
    const k = this.scaleK;
    const r = this.seededLocal( 5 );
    const n = Math.max( 4, Math.round( W * H / 70000 ) );
    for ( let i = 0; i < n; i++ ) {
      const th = r() * Math.PI * 2;
      ctx.save();
      ctx.globalAlpha = 0.55 + r() * 0.35;
      ctx.translate( r() * W, r() * H );
      ctx.scale( r() < 0.5 ? 1 : -1, 1 );
      this.macaw( ctx, ( 14 + r() * 9 ) * k, Math.sin( th ) * 0.85, Math.sin( th - 0.8 ) * 0.9, Math.floor( r() * 2 ) );
      ctx.restore();
    }
    ctx.globalAlpha = 1;
  }

  /**
   * The still parts, painted once, far to near: the sky and its clouds, the far hills and the Sugarloaf,
   * the ocean, the hills behind the city, the beach, the promenade, the avenue, the blocks, the palms and
   * the umbrellas.
   */
  private bake( scale: number ): void {
    const W = this.width;
    const H = this.height;
    const k = this.scaleK;
    const hz = this.hz;
    const F = this.F;
    const [ mint, sage, green, blue, yellow ] = this.five();
    const white: RGB = [ 255, 255, 255 ];
    const canvas = () => {
      const c = document.createElement( 'canvas' );
      c.width = Math.ceil( W * scale );
      c.height = Math.ceil( H * scale );
      const g2 = c.getContext( '2d' )!;
      g2.scale( scale, scale );
      return { c, g: g2 };
    };
    const { c, g } = canvas();
    const P = ( x: number, y: number, z: number ) => this.P( x, y, z );
    const dab = ( x: number, y: number, rx: number, ry: number, col: RGB, a: number ) => {
      g.save();
      g.translate( x, y );
      g.scale( Math.max( 0.1, rx ), Math.max( 0.1, ry ) );
      const gr = g.createRadialGradient( 0, 0, 0, 0, 0, 1 );
      gr.addColorStop( 0, this.rgba( col, a ) );
      gr.addColorStop( 1, this.rgba( col, 0 ) );
      g.fillStyle = gr;
      g.fillRect( -1, -1, 2, 2 );
      g.restore();
    };
    const blob = ( x: number, y: number, r: number, col: RGB, a = 1 ) => {
      const gr = g.createRadialGradient( x, y, 0, x, y, r );
      gr.addColorStop( 0, this.rgba( col, a ) );
      gr.addColorStop( 0.68, this.rgba( col, a * 0.92 ) );
      gr.addColorStop( 1, this.rgba( col, 0 ) );
      g.fillStyle = gr;
      g.fillRect( x - r, y - r, r * 2, r * 2 );
    };
    // one tree seen from a little above, lit from the upper left: its trunk, its crown's shadow side, its body, its
    // sunlit top, a fleck of light on the near ones
    const crownAt = ( x: number, y: number, r: number, base: RGB ) => {
      if ( r < 0.9 ) { blob( x, y, Math.max( 0.6, r ), base, 0.8 ); return; }
      // its trunk under it (owner), dark, lit on the sun's side
      g.fillStyle = this.rgba( this.mix( [ 92, 70, 52 ], base, 0.2 ), 1 );
      g.fillRect( x - r * 0.1, y + r * 0.35, r * 0.2, r * 0.85 );
      g.fillStyle = this.rgba( this.mix( [ 150, 124, 96 ], base, 0.2 ), 1 );
      g.fillRect( x - r * 0.1, y + r * 0.35, r * 0.07, r * 0.85 );
      blob( x + r * 0.18, y + r * 0.2, r, this.mix( base, [ 0, 40, 20 ], 0.35 ), 0.95 );
      blob( x - r * 0.05, y - r * 0.05, r * 0.78, base, 1 );
      blob( x - r * 0.3, y - r * 0.32, r * 0.42, this.mix( base, this.mix( sage, yellow, 0.5 ), 0.45 ), 0.9 );
      if ( r > 3 ) { blob( x - r * 0.42, y - r * 0.45, r * 0.16, this.mix( base, [ 255, 255, 220 ], 0.4 ), 0.8 ); }
    };
    const poly = ( pts: [ number, number ][], fill: string | CanvasGradient ) => {
      g.fillStyle = fill;
      g.beginPath();
      pts.forEach( ( [ x, y ], i ) => i ? g.lineTo( x, y ) : g.moveTo( x, y ) );
      g.closePath();
      g.fill();
    };
    // the air between us and a thing z metres off: 0 here, toward 1 at the horizon
    const hazeAt = ( z: number ) => 1 - Math.exp( -z / 3500 );
    const haze: RGB = this.mix( this.mix( yellow, white, 0.6 ), mint, 0.3 );
    const inAir = ( col: RGB, z: number, strength = 0.75 ) => this.mix( col, haze, hazeAt( z ) * strength );
    const zNear = this.zEnter();
    // samples along the beach, dense near and sparse far
    const zs: number[] = [];
    for ( let z = 12; z < 4200; z *= 1.02 ) { zs.push( z ); }

    // ---- the sky ----
    const sky = g.createLinearGradient( 0, 0, 0, hz );
    sky.addColorStop( 0, this.rgba( this.mix( blue, white, 0.35 ), 1 ) );
    sky.addColorStop( 0.65, this.rgba( this.mix( this.mix( blue, mint, 0.5 ), white, 0.4 ), 1 ) );
    sky.addColorStop( 1, this.rgba( haze, 1 ) );
    g.fillStyle = sky;
    g.fillRect( 0, 0, W, hz + 1 );
    // the sun high at the left, in the picture (owner), its warm glow round it; the glitter on the water
    // below lines up with it
    {
      const sx = SUN_U * H, sy = 0.12 * H;
      const sgl = g.createRadialGradient( sx, sy, 0, sx, sy, H * 0.75 );
      sgl.addColorStop( 0, this.rgba( this.mix( yellow, white, 0.6 ), 0.55 ) );
      sgl.addColorStop( 0.35, this.rgba( this.mix( yellow, white, 0.75 ), 0.18 ) );
      sgl.addColorStop( 1, this.rgba( white, 0 ) );
      g.fillStyle = sgl;
      g.fillRect( 0, 0, W, hz );
      // a soft sun, no hard rim (owner): bright at its heart, melting into its glow
      const r0 = 0.032 * H;
      const disc = g.createRadialGradient( sx, sy, 0, sx, sy, r0 * 3.2 );
      disc.addColorStop( 0, 'rgba(255,255,250,1)' );
      // its bright heart larger (owner)
      disc.addColorStop( 0.22, 'rgba(255,254,242,0.98)' );
      disc.addColorStop( 0.38, this.rgba( this.mix( yellow, white, 0.75 ), 0.7 ) );
      disc.addColorStop( 0.55, this.rgba( this.mix( yellow, white, 0.7 ), 0.3 ) );
      disc.addColorStop( 1, this.rgba( this.mix( yellow, white, 0.7 ), 0 ) );
      g.fillStyle = disc;
      g.fillRect( sx - r0 * 3.2, sy - r0 * 3.2, r0 * 6.4, r0 * 6.4 );
    }

    // ---- the ocean, below the horizon ----
    const sea = g.createLinearGradient( 0, hz, 0, H );
    sea.addColorStop( 0, this.rgba( this.mix( this.mix( blue, mint, 0.3 ), haze, 0.45 ), 1 ) );
    sea.addColorStop( 0.12, this.rgba( this.mix( blue, white, 0.2 ), 1 ) );
    sea.addColorStop( 0.5, this.rgba( this.mix( blue, [ 20, 150, 170 ], 0.35 ), 1 ) );
    sea.addColorStop( 1, this.rgba( this.mix( blue, [ 10, 120, 140 ], 0.45 ), 1 ) );
    g.fillStyle = sea;
    g.fillRect( 0, hz, W, H - hz );
    // the swell: level soft bands, closer together toward the horizon
    const wr = this.seededLocal( 37 );
    for ( let i = 0; i < 140; i++ ) {
      const z = 40 + Math.pow( wr(), 1.5 ) * 5000;
      const [ x, y ] = P( -200 - wr() * 4000, 0, z );
      if ( x > W + 50 ) { continue; }
      dab( x, y, F * ( 30 + wr() * 50 ) / z * 4, Math.max( 0.6, F * 1.2 / z ), wr() < 0.6 ? this.mix( mint, white, 0.4 ) : this.mix( blue, [ 0, 80, 110 ], 0.3 ), 0.25 );
    }
    {
      const cr3 = this.seededLocal( 39 );
      // the sun's glitter: a path of bright flecks on the water under the sun at the left, widening
      // toward us
      const sunX = SUN_U * H;
      for ( let i = 0; i < 700; i++ ) {
        const t = Math.pow( cr3(), 1.6 );
        const y = hz + 2 + t * ( H - hz ) * 0.7;
        const spread = ( 0.02 + t * 0.35 ) * H;
        const x = sunX + ( cr3() - 0.5 ) * 2 * spread * ( 0.4 + cr3() * 0.6 );
        const len = 1 + t * 10 * k;
        dab( x, y, len, Math.max( 0.5, len * 0.2 ), [ 255, 255, 245 ], 0.5 * ( 1 - t * 0.6 ) );
      }
    }
    // the haze lying on the horizon
    {
      const hg = g.createLinearGradient( 0, hz - 0.03 * H, 0, hz + 0.02 * H );
      hg.addColorStop( 0, this.rgba( haze, 0 ) );
      hg.addColorStop( 0.6, this.rgba( this.mix( haze, white, 0.3 ), 0.7 ) );
      hg.addColorStop( 1, this.rgba( haze, 0 ) );
      g.fillStyle = hg;
      g.fillRect( 0, hz - 0.03 * H, W, 0.05 * H );
    }
    // the shallows: paler turquoise along the shore
    {
      const pts: [ number, number ][] = [];
      for ( const z of zs ) { if ( z >= zNear * 0.8 ) { pts.push( P( this.shoreX( z ) - 50, 0, z ) ); } }
      for ( let i = zs.length - 1; i >= 0; i-- ) { if ( zs[ i ] >= zNear * 0.8 ) { pts.push( P( this.shoreX( zs[ i ] ), 0, zs[ i ] ) ); } }
      poly( pts, this.rgba( this.mix( mint, [ 60, 200, 200 ], 0.35 ), 0.35 ) );
    }

    // ---- the mountains across the water on the horizon, pale in the haze, two ranges ----
    {
      const range = ( u: number, i: number ) => {
        const f = 0.45 + 0.24 * Math.sin( u * 7.1 + i * 1.7 ) + 0.16 * Math.sin( u * 15.3 + i ) + 0.06 * Math.sin( u * 41 + i * 3 )
          + 0.4 * Math.exp( -Math.pow( ( u - 0.18 - i * 0.2 ) / 0.06, 2 ) ) + 0.3 * Math.exp( -Math.pow( ( u - 0.42 + i * 0.1 ) / 0.05, 2 ) );
        return Math.max( 0, Math.min( 1.3, f ) );
      };
      const tone: RGB = this.mix( blue, sage, 0.35 );
      for ( let i = 0; i < 2; i++ ) {
        const hMax = [ 0.06, 0.04 ][ i ] * H;
        const col = this.mix( tone, haze, [ 0.72, 0.58 ][ i ] );
        const gr = g.createLinearGradient( 0, hz - hMax * 1.3, 0, hz );
        gr.addColorStop( 0, this.rgba( col, 1 ) );
        gr.addColorStop( 1, this.rgba( this.mix( col, haze, 0.4 ), 1 ) );
        g.fillStyle = gr;
        g.beginPath();
        g.moveTo( -5, hz + 1 );
        // across the open water, up to the far end of the beach
        const xEnd = this.vx - 0.02 * H;
        for ( let x = -5; x <= xEnd; x += 3 ) {
          const fade = Math.min( 1, ( xEnd - x ) / ( 0.12 * H ) );
          g.lineTo( x, hz + 1 - hMax * range( x / ( 1.75 * H ), i ) * fade );
        }
        g.lineTo( xEnd, hz + 1 );
        g.closePath();
        g.fill();
      }
      // the Cagarras islands, small and grey, off the beach
      for ( const [ ix, iw, ih ] of [ [ 1.2, 0.05, 0.012 ], [ 1.0, 0.03, 0.008 ], [ 0.88, 0.02, 0.006 ] ] ) {
        const x = this.vx - ix * H, y = hz + 0.004 * H;
        g.fillStyle = this.rgba( this.mix( tone, haze, 0.5 ), 1 );
        g.beginPath(); g.moveTo( x - iw * H, y ); g.quadraticCurveTo( x - iw * H * 0.2, y - ih * H * 2, x + iw * H * 0.3, y - ih * H ); g.quadraticCurveTo( x + iw * H * 0.8, y - ih * H * 0.4, x + iw * H, y ); g.closePath(); g.fill();
      }
    }

    // ---- the far end: the Sugarloaf and the Urca beyond Leme, bigger and drawn with care: the bare
    // granite dome lit from the right and streaked, forest round its foot; the Urca's forested back ----
    {
      const z0 = 4300, x0 = 40;
      // bigger (owner)
      const loafH = 760, loafW = 200;
      const loaf = ( u: number ) => Math.abs( u ) >= 1 ? 0 : loafH * Math.pow( 1 - Math.pow( Math.abs( u ), 2.3 ), 0.5 );
      const loafPts: [ number, number ][] = [];
      for ( let u = -1.5; u <= 1.5; u += 0.01 ) {
        const hgt = Math.max( loaf( u + 0.05 * u * u ), 130 * Math.max( 0, 1 - Math.abs( u ) / 1.5 ) );
        loafPts.push( P( x0 + u * loafW, hgt, z0 ) );
      }
      loafPts.push( P( x0 + 1.5 * loafW, 0, z0 ), P( x0 - 1.5 * loafW, 0, z0 ) );
      const rock = inAir( this.mix( [ 108, 104, 98 ], sage, 0.1 ), z0, 0.55 );
      const rockLit = inAir( this.mix( [ 184, 168, 156 ], yellow, 0.06 ), z0, 0.55 );
      const forestFar = inAir( this.mix( green, sage, 0.35 ), z0, 0.55 );
      const [ lx ] = P( x0 - loafW, 0, z0 ), [ rx ] = P( x0 + loafW, 0, z0 );
      const lg = g.createLinearGradient( lx, 0, rx, 0 );
      lg.addColorStop( 0, this.rgba( rockLit, 1 ) );
      lg.addColorStop( 0.4, this.rgba( this.mix( rock, rockLit, 0.5 ), 1 ) );
      lg.addColorStop( 1, this.rgba( rock, 1 ) );
      g.save();
      poly( loafPts, lg );
      g.beginPath();
      loafPts.forEach( ( [ x, y ], i ) => i ? g.lineTo( x, y ) : g.moveTo( x, y ) );
      g.closePath();
      g.clip();
      const sr2 = this.seededLocal( 71 );
      const pxm = F / z0;
      for ( let i = 0; i < 26; i++ ) {
        const u = sr2() * 1.8 - 0.9;
        const [ x, y ] = P( x0 + u * loafW, loaf( u ) * ( 0.25 + sr2() * 0.6 ), z0 );
        dab( x, y, Math.max( 0.6, pxm * ( 3 + sr2() * 5 ) ), Math.max( 1, pxm * ( 40 + sr2() * 70 ) ), this.mix( rock, [ 50, 46, 44 ], 0.35 ), 0.16 );
      }
      // the forest round its foot and up its seaward shoulder
      {
        const trees: [ number, number, number, RGB ][] = [];
        // dense at its foot (owner)
        for ( let i = 0; i < 1800; i++ ) {
          const u = sr2() * 3 - 1.5;
          const top = Math.max( 0, 130 * ( 1 - Math.abs( u ) / 1.5 ) ) * ( 0.6 + 0.4 * sr2() ) + ( Math.abs( u ) < 1 ? loaf( u ) * 0.18 : 0 );
          const [ x, y ] = P( x0 + u * loafW, top * sr2(), z0 );
          trees.push( [ x, y, Math.max( 0.7, pxm * ( 7 + sr2() * 7 ) ), this.mix( forestFar, sr2() < 0.5 ? [ 0, 50, 26 ] : this.mix( sage, yellow, 0.3 ), sr2() * 0.25 ) ] );
        }
        trees.sort( ( a, b ) => a[ 1 ] - b[ 1 ] );
        for ( const [ x, y, r2, tone ] of trees ) { crownAt( x, y, r2, tone ); }
      }
      g.restore();
      // the Urca, lower, before it at the right: its rounded forested back, a granite face to the sea
      const uz = 4100, ux = x0 + 500, uw = 280, uh = 400;
      const urca = ( u: number ) => Math.max( 0, uh * Math.pow( Math.max( 0, 1 - u * u ), 0.75 ) * ( 1 + 0.05 * Math.sin( u * 8 ) ) );
      const uPts: [ number, number ][] = [];
      for ( let u = -1; u <= 1.001; u += 0.01 ) { uPts.push( P( ux + u * uw, urca( u ), uz ) ); }
      const fU = inAir( this.mix( green, sage, 0.35 ), uz, 0.55 );
      g.save();
      poly( uPts, this.rgba( fU, 1 ) );
      g.beginPath();
      uPts.forEach( ( [ x, y ], i ) => i ? g.lineTo( x, y ) : g.moveTo( x, y ) );
      g.closePath();
      g.clip();
      const pxu = F / uz;
      // lit toward the sun at the right, shaded at the left
      {
        const [ ulx ] = P( ux - uw, 0, uz ), [ urx ] = P( ux + uw, 0, uz );
        const sh = g.createLinearGradient( ulx, 0, urx, 0 );
        sh.addColorStop( 0, 'rgba(255,250,210,0.12)' );
        sh.addColorStop( 0.45, 'rgba(0,40,20,0)' );
        sh.addColorStop( 1, 'rgba(0,40,20,0.22)' );
        g.fillStyle = sh;
        g.fillRect( ulx, 0, urx - ulx, H );
      }
      // spurs and gullies down its flanks
      for ( let i = 0; i < 14; i++ ) {
        const u = -0.9 + i * 0.13 + sr2() * 0.04;
        const [ x, y ] = P( ux + u * uw, urca( u ) * 0.55, uz );
        const [ , y0 ] = P( ux + u * uw, urca( u ), uz );
        dab( x, y, Math.max( 1, pxu * 14 ), ( y - y0 ) * 1.1, i % 2 ? this.mix( fU, [ 0, 46, 24 ], 0.35 ) : this.mix( fU, this.mix( sage, yellow, 0.3 ), 0.3 ), 0.35 );
      }
      {
        const trees: [ number, number, number, RGB ][] = [];
        for ( let i = 0; i < 1500; i++ ) {
          const u = sr2() * 2 - 1;
          const [ x, y ] = P( ux + u * uw, urca( u ) * Math.pow( sr2(), 0.6 ), uz );
          // the shaded side at the left darker
          const tone = this.mix( this.mix( fU, [ 0, 46, 24 ], Math.max( 0, u ) * 0.25 ), sr2() < 0.5 ? [ 0, 50, 26 ] : this.mix( sage, yellow, 0.3 ), sr2() * 0.25 );
          trees.push( [ x, y, Math.max( 0.7, pxu * ( 7 + sr2() * 7 ) ), tone ] );
        }
        trees.sort( ( a, b ) => a[ 1 ] - b[ 1 ] );
        for ( const [ x, y, r2, tone ] of trees ) { crownAt( x, y, r2, tone ); }
      }
      // its granite face toward the sea, at the left, with streaks down it
      for ( let i = 0; i < 26; i++ ) {
        const u = -0.85 + sr2() * 0.35;
        const [ x, y ] = P( ux + u * uw, urca( u ) * ( 0.2 + sr2() * 0.55 ), uz );
        dab( x, y, Math.max( 1, pxu * ( 18 + sr2() * 20 ) ), Math.max( 1, pxu * ( 30 + sr2() * 40 ) ), inAir( [ 178, 160, 150 ], uz, 0.5 ), 0.6 );
      }
      g.restore();
      // crowns along its skyline
      for ( let u = -0.98; u <= 0.98; u += 0.01 ) {
        const [ x, y ] = P( ux + u * uw, urca( u ), uz );
        blob( x, y + 0.5, Math.max( 0.7, pxu * ( 5 + sr2() * 5 ) ), this.mix( fU, sr2() < 0.5 ? [ 0, 46, 24 ] : this.mix( sage, yellow, 0.3 ), 0.2 ), 0.85 );
      }
      // the cable car's line from the Urca's top to the Sugarloaf's summit, catching the light
      const [ ax, ay ] = P( ux - 0.15 * uw, urca( -0.15 ), uz ), [ bx, by ] = P( x0 + 0.03 * loafW, loafH * 0.995, z0 );
      this.cable = [ ax, ay, ( ax + bx ) / 2, ( ay + by ) / 2 + 0.008 * H, bx, by ];
      g.strokeStyle = 'rgba(80,84,88,0.6)';
      g.lineWidth = 0.7;
      g.beginPath(); g.moveTo( ax, ay ); g.quadraticCurveTo( ( ax + bx ) / 2, ( ay + by ) / 2 + 0.008 * H, bx, by ); g.stroke();
      g.fillStyle = 'rgb(236,236,230)';
      g.fillRect( ax - 2, ay - 1.5, 4, 2.5 );
      g.fillRect( bx - 2, by - 1.5, 4, 2.5 );
      // the Leme hill closing the beach, forested, its foot in the water
      const pts3: [ number, number ][] = [];
      for ( let u = -1; u <= 1; u += 0.02 ) { pts3.push( P( this.shoreX( 3900 ) + 160 + u * 260, 190 * Math.pow( Math.max( 0, 1 - u * u ), 0.7 ) * ( 1 + 0.05 * Math.sin( u * 9 ) ), 3950 ) ); }
      const fL = inAir( this.mix( green, sage, 0.3 ), 3950, 0.65 );
      poly( pts3, this.rgba( fL, 1 ) );
      g.save();
      g.beginPath();
      pts3.forEach( ( [ x, y ], j ) => j ? g.lineTo( x, y ) : g.moveTo( x, y ) );
      g.closePath();
      g.clip();
      const lmT: [ number, number, number, RGB ][] = [];
      for ( let i = 0; i < 700; i++ ) {
        const u = sr2() * 2 - 1;
        const [ x, y ] = P( this.shoreX( 3900 ) + 160 + u * 260, 190 * Math.pow( Math.max( 0, 1 - u * u ), 0.7 ) * Math.pow( sr2(), 0.6 ), 3950 );
        lmT.push( [ x, y, Math.max( 0.7, F * ( 7 + sr2() * 6 ) / 3950 ), this.mix( fL, sr2() < 0.5 ? [ 0, 50, 26 ] : this.mix( sage, yellow, 0.3 ), sr2() * 0.25 ) ] );
      }
      lmT.sort( ( a, b ) => a[ 1 ] - b[ 1 ] );
      for ( const [ x, y, r2, tone ] of lmT ) { crownAt( x, y, r2, tone ); }
      g.restore();
    }

    // ---- the hills behind the city, two ranges, the farther paler ----
    // the ranges come down gently toward the end of the beach and sink behind the low far blocks there, so
    // no sliver of them stands up over the far roofs (owner)
    const ridgeFall = ( z: number ) => { const t = Math.max( 0, Math.min( 1, ( z - 1700 ) / 1500 ) ); return 1 - t * t * ( 3 - 2 * t ); };
    const ridge = ( z: number, i: number ) => {
      const t = z / 4000;
      return ( ( i ? 360 : 300 ) + 110 * Math.sin( t * 9 + i * 2 ) + 60 * Math.sin( t * 23 + i ) + 30 * Math.sin( t * 51 + i * 3 ) ) * ridgeFall( z );
    };
    for ( const i of [ 1, 0 ] ) {
      const back = i ? 900 : 320;
      const pts: [ number, number ][] = [];
      for ( const z of zs ) { if ( z > 120 ) { pts.push( P( this.frontX( z ) + back, ridge( z, i ), z ) ); } }
      // past the end of the beach the range comes round to Leme
      // past the end of the beach the range sinks to the horizon, no cut edge (owner: that green column)
      for ( let a = 0; a <= 1; a += 0.05 ) { pts.push( P( this.frontX( 4200 ) + back, ridge( 4200, i ) * Math.pow( 1 - a, 1.6 ), 4200 + 1600 * a ) ); }
      pts.push( [ W + 10, hz + 2 ], [ W + 10, H ] );
      pts.push( P( this.frontX( 120 ) + back, 0, 120 ) );
      const col = inAir( this.mix( green, sage, i ? 0.55 : 0.3 ), i ? 2600 : 1400, 0.9 );
      // the ground of the hills bare brown granite, as on the islands (owner); the forest grows over it in
      // clumps, the rock showing between them
      poly( pts, this.rgba( inAir( this.mix( [ 150, 120, 96 ], sage, 0.08 ), i ? 2600 : 1400, 0.9 ), 1 ) );
      // the lower slopes bare brown granite, as on the Sugarloaf (owner), the forest above it
      {
        const rockC = inAir( this.mix( [ 150, 120, 96 ], sage, 0.08 ), i ? 2600 : 1400, 0.9 );
        const rp: [ number, number ][] = [];
        for ( const z of zs ) { if ( z > 120 ) { rp.push( P( this.frontX( z ) + back, ridge( z, i ) * ( 0.42 + 0.12 * Math.sin( z * 0.004 + i ) + 0.06 * Math.sin( z * 0.017 ) ), z ) ); } }
        for ( let a = 0; a <= 1; a += 0.05 ) { rp.push( P( this.frontX( 4200 ) + back, ridge( 4200, i ) * 0.42 * Math.pow( 1 - a, 1.6 ), 4200 + 1600 * a ) ); }
        rp.push( [ W + 10, hz + 2 ], [ W + 10, H ] );
        rp.push( P( this.frontX( 120 ) + back, 0, 120 ) );
        poly( rp, this.rgba( rockC, 1 ) );
      }
      // the relief, on both ranges (the farther one lighter): spurs lit toward the sun, gullies in shade
      // running down the fall line, granite showing near the tops, crowns along the skyline, a soft mist
      // lying at their feet behind the blocks; then the forest's grain
      {
        const fr = this.seededLocal( 41 + i );
        g.save();
        g.beginPath();
        pts.forEach( ( [ x, y ], j ) => j ? g.lineTo( x, y ) : g.moveTo( x, y ) );
        g.closePath();
        g.clip();
        const dark: RGB = this.mix( col, [ 0, 46, 24 ], i ? 0.12 : 0.28 ), lit: RGB = this.mix( col, this.mix( sage, yellow, 0.4 ), i ? 0.15 : 0.3 );
        for ( let z = 160; z < 4200; z *= 1.035 ) {
          const [ tx, ty ] = P( this.frontX( z ) + back, ridge( z, i ), z );
          const [ , fy ] = P( this.frontX( z ) + back, 0, z );
          const slope = ridge( z + 30, i ) - ridge( z - 30, i );
          const spurW = Math.max( 2, ( P( this.frontX( z * 1.035 ) + back, 0, z * 1.035 )[ 0 ] - tx ) * 0.9 );
          dab( tx, ( ty + fy ) / 2, spurW * 0.8, ( fy - ty ) * 0.55, slope < 0 ? dark : lit, i ? 0.18 : 0.3 );
          if ( fr() < 0.5 ) { dab( tx + spurW * 0.3, ty + ( fy - ty ) * 0.45, Math.max( 0.8, spurW * 0.12 ), ( fy - ty ) * 0.4, dark, i ? 0.2 : 0.35 ); }
          if ( !i && fr() < 0.18 ) { dab( tx + ( fr() - 0.5 ) * spurW, ty + ( fy - ty ) * ( 0.08 + fr() * 0.15 ), spurW * 0.35, ( fy - ty ) * 0.1, inAir( [ 168, 160, 150 ], z + back, 0.8 ), 0.5 ); }
        }
        g.restore();
        // the forest as crowns (not clipped, so the ridge is their crowns, owner), set out from the tops down so the lower (nearer) overlap the higher
        {
          const trees: [ number, number, number, RGB ][] = [];
          const n = i ? 3000 : 10000;
          for ( let j = 0; j < n; j++ ) {
            const z = 150 + Math.pow( fr(), 1.2 ) * 4000;
            const hf = 0.4 + 0.6 * Math.pow( fr(), 0.6 );
            // the clumps: a slow pattern over the slopes, bare rock where it is low
            const clump = Math.sin( z * 0.006 + hf * 7 + i ) + Math.sin( z * 0.017 - hf * 11 ) * 0.6;
            // more trees, the rock only in a few patches (owner)
            if ( clump < -1.45 ) { fr(); fr(); fr(); continue; }
            const [ x, y ] = P( this.frontX( z ) + back, ridge( z, i ) * hf, z );
            // each tree its own green (owner): deep, mid, sage or yellowish, then steeped in the air
            const greens: RGB[] = [ [ 22, 120, 66 ], [ 52, 140, 74 ], [ 96, 160, 88 ], [ 132, 178, 98 ], [ 64, 128, 60 ], [ 150, 186, 96 ] ];
            const tone = inAir( this.mix( greens[ Math.floor( fr() * greens.length ) ], col, 0.25 + fr() * 0.2 ), ( i ? 2600 : 1400 ), i ? 0.5 : 0.25 );
            trees.push( [ x, y, Math.max( 0.7, F * ( 7 + fr() * 6 ) / ( z + back ) ), tone ] );
          }
          trees.sort( ( a, b ) => a[ 1 ] - b[ 1 ] );
          for ( const [ x, y, r2, tone ] of trees ) { crownAt( x, y, r2, tone ); }
        }
        // the mist at their feet
        const mist = g.createLinearGradient( 0, hz - 0.12 * H, 0, hz + 0.02 * H );
        mist.addColorStop( 0, this.rgba( haze, 0 ) );
        mist.addColorStop( 1, this.rgba( haze, i ? 0.5 : 0.35 ) );
        g.save();
        g.beginPath();
        pts.forEach( ( [ x, y ], j ) => j ? g.lineTo( x, y ) : g.moveTo( x, y ) );
        g.closePath();
        g.clip();
        g.fillStyle = mist;
        g.fillRect( 0, hz - 0.12 * H, W, 0.14 * H );
        g.restore();
      }
    }

    // ---- the land: the beach to the right of the water's edge, all the way down ----
    {
      const pts: [ number, number ][] = [];
      for ( const z of zs ) { if ( z >= zNear * 0.7 ) { pts.push( P( this.shoreX( z ), 0, z ) ); } }
      pts.push( P( this.shoreX( 4200 ) + 400, 0, 4200 ), [ W + 10, hz + 1 ], [ W + 10, H + 10 ], [ -10, H + 10 ] );
      const sand = g.createLinearGradient( 0, hz, 0, H );
      sand.addColorStop( 0, this.rgba( inAir( [ 238, 226, 196 ], 3000 ), 1 ) );
      sand.addColorStop( 0.3, 'rgb(240,228,198)' );
      sand.addColorStop( 1, 'rgb(246,236,210)' );
      poly( pts, sand );
      // the wet sand along the water
      const wet: [ number, number ][] = [];
      for ( const z of zs ) { if ( z >= zNear * 0.7 ) { wet.push( P( this.shoreX( z ), 0, z ) ); } }
      for ( let i = zs.length - 1; i >= 0; i-- ) { if ( zs[ i ] >= zNear * 0.7 ) { wet.push( P( this.shoreX( zs[ i ] ) + 8, 0, zs[ i ] ) ); } }
      poly( wet, 'rgba(206,188,150,0.55)' );
      // the wet sand's gloss: the sky's light lying on it in a pale sheen along the water
      const gloss: [ number, number ][] = [];
      for ( const z of zs ) { if ( z >= zNear * 0.7 ) { gloss.push( P( this.shoreX( z ) + 0.5, 0, z ) ); } }
      for ( let i = zs.length - 1; i >= 0; i-- ) { if ( zs[ i ] >= zNear * 0.7 ) { gloss.push( P( this.shoreX( zs[ i ] ) + 4, 0, zs[ i ] ) ); } }
      poly( gloss, this.rgba( this.mix( [ 210, 220, 222 ], blue, 0.12 ), 0.4 ) );
      // the sand's ripples, wind lines across it, and footprints: broad near us, finer far off
      const sr = this.seededLocal( 43 );
      for ( let i = 0; i < 3200; i++ ) {
        const z = 14 + Math.pow( sr(), 2.6 ) * 900;
        const x = this.shoreX( z ) + 10 + sr() * 72;
        const [ px, py ] = P( x, 0, z );
        if ( sr() < 0.3 ) {
          // a footprint pair
          const fs = F * 0.14 / z;
          dab( px, py, fs * 1.1, fs * 0.45, [ 212, 194, 156 ], 0.55 );
          dab( px + fs * 1.8, py - fs * 0.3, fs * 1.1, fs * 0.45, [ 212, 194, 156 ], 0.55 );
        } else {
          // a ripple: its shaded side and its lit crest just above
          const rw = F * ( 1.4 + sr() * 2 ) / z, rh = Math.max( 0.4, F * 0.08 / z );
          dab( px, py, rw, rh * 1.6, [ 218, 202, 166 ], 0.45 );
          dab( px - rw * 0.1, py - rh * 1.4, rw * 0.9, rh, [ 255, 250, 232 ], 0.5 );
        }
      }
    }

    // a strip on the ground between two lines across (x0, x1 as functions of z), from z0 to z1
    const strip = ( xa: ( z: number ) => number, xb: ( z: number ) => number, z0: number, z1: number, fill: string ) => {
      const pts: [ number, number ][] = [];
      for ( const z of zs ) { if ( z >= z0 && z <= z1 ) { pts.push( P( xa( z ), 0, z ) ); } }
      for ( let i = zs.length - 1; i >= 0; i-- ) { if ( zs[ i ] >= z0 && zs[ i ] <= z1 ) { pts.push( P( xb( zs[ i ] ), 0, zs[ i ] ) ); } }
      poly( pts, fill );
    };
    const zEndCity = 3600;
    // ---- the promenade: Copacabana's black and white waves, the stone kerb ----
    strip( z => this.promX( z ), z => this.promX( z ) + 9, 12, zEndCity, 'rgb(236,234,226)' );
    {
      // the wave bands, drawn where they are big enough to read, grey beyond
      for ( let band = 0; band < 3; band++ ) {
        const pts: [ number, number ][] = [];
        const inner: [ number, number ][] = [];
        for ( let z = 12; z < 900; z += Math.max( 0.25, z * 0.004 ) ) {
          const u = 1.6 + band * 2.9 + 1.0 * Math.sin( z * Math.PI * 2 / 6 );
          pts.push( P( this.promX( z ) + u, 0, z ) );
          inner.push( P( this.promX( z ) + u + 1.1, 0, z ) );
        }
        poly( [ ...pts, ...inner.reverse() ], 'rgb(46,46,48)' );
      }
      strip( z => this.promX( z ) + 1.5, z => this.promX( z ) + 8, 900, zEndCity, 'rgba(140,140,140,0.6)' );
    }
    // ---- the avenue: two carriageways, the median, the pavement before the blocks ----
    strip( z => this.promX( z ) + 9, z => this.promX( z ) + 20, 12, zEndCity, 'rgb(118,120,122)' );
    strip( z => this.promX( z ) + 20, z => this.promX( z ) + 22, 12, zEndCity, 'rgb(212,210,202)' );
    strip( z => this.promX( z ) + 22, z => this.promX( z ) + 33, 12, zEndCity, 'rgb(118,120,122)' );
    strip( z => this.promX( z ) + 33, z => this.frontX( z ), 12, zEndCity, 'rgb(226,222,212)' );
    // the lane lines, dashed in metres
    for ( const lx of [ 14.5, 27.5 ] ) {
      for ( let z = 14; z < 900; z += 6 ) {
        const a = P( this.promX( z ) + lx, 0, z ), b = P( this.promX( z + 3 ) + lx, 0, z + 3 );
        g.strokeStyle = 'rgba(250,250,240,0.7)';
        g.lineWidth = Math.max( 0.4, F * 0.15 / z );
        g.beginPath(); g.moveTo( a[ 0 ], a[ 1 ] ); g.lineTo( b[ 0 ], b[ 1 ] ); g.stroke();
      }
    }
    // ---- the blocks along the avenue, facing the sea: far ones first ----
    {
      const walls: RGB[] = [ [ 246, 244, 236 ], [ 241, 230, 204 ], [ 242, 223, 168 ], [ 236, 207, 194 ], [ 222, 226, 222 ], [ 230, 214, 190 ], [ 248, 248, 244 ], [ 214, 226, 220 ] ];
      const br = this.seededLocal( 53 );
      const blocks: { z0: number; z1: number; h: number; wall: RGB; depth: number; balc: boolean }[] = [];
      for ( let z = 60; z < zEndCity; ) {
        const len = 18 + br() * 22, gap = 1 + br() * 4;
        const tall = br();
        // heights of their own: low older blocks, the usual twelve floors, a few towers (owner: not all alike)
        const h = tall < 0.12 ? 52 + br() * 16 : tall < 0.35 ? 22 + br() * 8 : 32 + br() * 12;
        // the Copacabana Palace: long, white, eight floors, set back a little, at its place along the beach
        if ( z > 1350 && z < 1400 ) {
          blocks.push( { z0: z, z1: z + 110, h: 27, wall: [ 252, 252, 248 ], depth: 40, balc: true } );
          z += 110 + gap;
          continue;
        }
        blocks.push( { z0: z, z1: z + len, h, wall: walls[ Math.floor( br() * walls.length ) ], depth: 20 + br() * 8, balc: br() < 0.6 } );
        z += len + gap;
      }
      for ( let i = blocks.length - 1; i >= 0; i-- ) {
        const b = blocks[ i ];
        const zm = ( b.z0 + b.z1 ) / 2;
        const wall = inAir( b.wall, zm, 0.7 );
        const xf0 = this.frontX( b.z0 ), xf1 = this.frontX( b.z1 );
        const A = P( xf0, 0, b.z0 ), B = P( xf0, b.h, b.z0 ), C = P( xf1, b.h, b.z1 ), D = P( xf1, 0, b.z1 );
        if ( A[ 0 ] > W + 40 && D[ 0 ] > W + 40 ) { continue; }
        // the near end wall, facing us, in shade
        const E = P( xf0 + b.depth, b.h, b.z0 ), G = P( xf0 + b.depth, 0, b.z0 );
        poly( [ A, B, E, G ], this.rgba( this.mix( wall, [ 90, 92, 88 ], 0.3 ), 1 ) );
        // the front, lit by the noon sun over the sea
        const fg = g.createLinearGradient( A[ 0 ], 0, D[ 0 ], 0 );
        fg.addColorStop( 0, this.rgba( this.mix( wall, white, 0.12 ), 1 ) );
        fg.addColorStop( 1, this.rgba( wall, 1 ) );
        poly( [ A, B, C, D ], fg );
        // the windows: floors of 3 m, bays of 3.6 m, each a pane with the sky's light in its top; the
        // balconies' slabs under them; plain bands where they are too small to read
        const pxPerM = ( A[ 1 ] - B[ 1 ] ) / b.h;
        const floors = Math.floor( ( b.h - 4 ) / 3 );
        if ( pxPerM > 1.1 ) {
          const bays = Math.max( 1, Math.round( ( b.z1 - b.z0 ) / 3.6 ) );
          for ( let fl = 0; fl < floors; fl++ ) {
            const y0 = 4 + fl * 3 + 0.9, y1 = y0 + 1.6;
            for ( let bi = 0; bi < bays; bi++ ) {
              const za = b.z0 + ( b.z1 - b.z0 ) * ( bi + 0.15 ) / bays, zb = b.z0 + ( b.z1 - b.z0 ) * ( bi + 0.85 ) / bays;
              const q1 = P( this.frontX( za ), y1, za ), q2 = P( this.frontX( zb ), y1, zb ), q3 = P( this.frontX( zb ), y0, zb ), q4 = P( this.frontX( za ), y0, za );
              const pane = g.createLinearGradient( 0, q1[ 1 ], 0, q4[ 1 ] );
              pane.addColorStop( 0, this.rgba( inAir( [ 172, 202, 222 ], zm, 0.6 ), 1 ) );
              pane.addColorStop( 1, this.rgba( inAir( [ 74, 92, 108 ], zm, 0.6 ), 1 ) );
              poly( [ q1, q2, q3, q4 ], pane );
            }
            if ( b.balc && pxPerM > 2.2 ) {
              const s1 = P( xf0 - 0.2, y0 - 0.3, b.z0 ), s2 = P( xf1 - 0.2, y0 - 0.3, b.z1 ), s3 = P( xf1 - 0.2, y0 - 0.65, b.z1 ), s4 = P( xf0 - 0.2, y0 - 0.65, b.z0 );
              poly( [ s1, s2, s3, s4 ], this.rgba( this.mix( wall, white, 0.6 ), 1 ) );
            }
          }
          // the ground floor's shops: dark glass under a coloured awning
          const trims: RGB[] = [ [ 236, 218, 97 ], [ 19, 161, 102 ], [ 34, 135, 213 ], [ 242, 112, 154 ], [ 242, 140, 58 ] ];
          const aw = trims[ i % trims.length ];
          poly( [ P( xf0, 3, b.z0 ), P( xf1, 3, b.z1 ), P( xf1, 0.3, b.z1 ), P( xf0, 0.3, b.z0 ) ], this.rgba( inAir( [ 46, 54, 60 ], zm, 0.6 ), 1 ) );
          poly( [ P( xf0 - 1.2, 3.4, b.z0 ), P( xf1 - 1.2, 3.4, b.z1 ), P( xf1, 3.9, b.z1 ), P( xf0, 3.9, b.z0 ) ], this.rgba( inAir( aw, zm, 0.6 ), 1 ) );
          // the end wall's small windows
          if ( pxPerM > 2 ) {
            for ( let fl = 0; fl < floors; fl++ ) {
              const y0 = 4 + fl * 3 + 0.9;
              for ( const dxw of [ 0.25, 0.6 ] ) {
                const wx = xf0 + b.depth * dxw;
                poly( [ P( wx, y0 + 1.5, b.z0 ), P( wx + 2.2, y0 + 1.5, b.z0 ), P( wx + 2.2, y0, b.z0 ), P( wx, y0, b.z0 ) ], 'rgba(60,72,84,0.55)' );
              }
            }
          }
        } else if ( pxPerM > 0.25 ) {
          g.strokeStyle = this.rgba( inAir( [ 110, 124, 136 ], zm, 0.6 ), 0.5 );
          g.lineWidth = Math.max( 0.4, pxPerM * 1.2 );
          for ( let fl = 0; fl < floors; fl += pxPerM > 0.6 ? 1 : 2 ) {
            const a = P( xf0, 5.5 + fl * 3, b.z0 ), d = P( xf1, 5.5 + fl * 3, b.z1 );
            g.beginPath(); g.moveTo( a[ 0 ], a[ 1 ] ); g.lineTo( d[ 0 ], d[ 1 ] ); g.stroke();
          }
        }
        // the parapet along the top, lit
        poly( [ B, C, P( xf1, b.h - 0.5, b.z1 ), P( xf0, b.h - 0.5, b.z0 ) ], this.rgba( this.mix( wall, white, 0.5 ), 1 ) );
      }
    }

    // ---- the palms along the beach side of the promenade, and the umbrellas and people on the sand,
    // all in one far-to-near order so the nearer stand in front ----
    {
      type Item = { z: number; draw: () => void };
      const items: Item[] = [];
      // the palms on a layer of their own, for the wind (owner)
      const pl = canvas();
      const palmsTodo: Item[] = [];
      const pr = this.seededLocal( 59 );
      for ( let z = 22; z < zEndCity; z += 16 + pr() * 6 ) {
        const lean = ( pr() - 0.5 ) * 1.6, tall = 9 + pr() * 4, seed = Math.floor( pr() * 1e6 );
        // (owner: no shadows under the palms, they spoiled the view)
        palmsTodo.push( { z, draw: () => this.palm( pl.g, P, this.promX( z ) - 1.5, z, tall, lean, seed, inAir( this.mix( green, sage, 0.2 ), z, 0.7 ) ) } );
      }
      const ur = this.seededLocal( 61 );
      // no white ones (owner)
      const umbr: RGB[] = [ [ 242, 112, 154 ], [ 236, 218, 97 ], [ 34, 135, 213 ], [ 19, 161, 102 ], [ 242, 140, 58 ] ];
      // the umbrellas, each with one or two sunbeds beside it (owner: no people; umbrellas leaning every
      // way, as they really stand)
      // the one volleyball net (owner) and its court, kept clear of umbrellas and sunbeds
      const netZ = 95, netX = this.shoreX( netZ ) + 34;
      for ( let i = 0; i < 1150; i++ ) {
        const z = 20 + Math.pow( ur(), 1.2 ) * 2600;
        const x = this.shoreX( z ) + 14 + ur() * 62;
        if ( x > netX - 6 && x < netX + 15 && z > netZ - 14 && z < netZ + 14 ) { ur(); ur(); ur(); ur(); ur(); ur(); continue; }
        const col = umbr[ Math.floor( ur() * umbr.length ) ];
        const tiltA = ( ur() - 0.5 ) * 0.7, tiltDir = ur() * Math.PI * 2;
        const beds = ur() < 0.7 ? 1 + ( ur() < 0.5 ? 1 : 0 ) : 0;
        const bedCol: RGB = umbr[ Math.floor( ur() * umbr.length ) ];
        const bedSide = ur() < 0.5 ? -1 : 1;
        items.push( { z, draw: () => {
          const s = F / z;
          if ( s * 1.2 < 1 ) { const [ px, py ] = P( x, 2.2, z ); blob( px, py, Math.max( 0.6, s * 1.2 ), inAir( col, z ), 0.9 ); return; }
          // the umbrella: its pole leaning, the canopy a ring of segments seen from above, alternately the
          // colour and white, lit on its sunward side; its shadow on the sand
          const tilt = ( lx: number, ly: number, lz: number ): [ number, number, number ] => {
            const ca = Math.cos( tiltA ), sa = Math.sin( tiltA ), dx = Math.cos( tiltDir ), dz = Math.sin( tiltDir );
            // rotate about the horizontal axis perpendicular to tiltDir
            const along = lx * dx + lz * dz, perp = -lx * dz + lz * dx;
            const a2 = along * ca + ly * sa, y2 = -along * sa + ly * ca;
            return [ x + a2 * dx - perp * dz, y2, z + a2 * dz + perp * dx ];
          };
          const top = tilt( 0, 2.3, 0 );
          // its shadow: the canopy and the pole cast on the sand along the sun's rays (owner: by the sun's
          // angle), the sun high at the upper right
          const sun = ( pt: [ number, number, number ] ): [ number, number ] => P( pt[ 0 ] + SUNX * pt[ 1 ], 0, pt[ 2 ] + SUNZ * pt[ 1 ] );
          const shPts: [ number, number ][] = [];
          for ( let q = 0; q < 16; q++ ) { const a = q / 16 * Math.PI * 2; shPts.push( sun( tilt( Math.cos( a ) * 1.25, 2.25, Math.sin( a ) * 1.25 ) ) ); }
          const shadeSand = this.rgba( inAir( [ 214, 196, 160 ], z ), 1 );
          poly( shPts, shadeSand );
          const [ bx, by ] = P( x, 0, z ), [ tx, ty ] = P( top[ 0 ], top[ 1 ], top[ 2 ] );
          const [ psx, psy ] = sun( top );
          g.strokeStyle = shadeSand;
          g.lineWidth = Math.max( 0.5, s * 0.06 );
          g.beginPath(); g.moveTo( bx, by ); g.lineTo( psx, psy ); g.stroke();
          // the sunbeds, after the umbrella's shadow so they lie in it (owner), beside the pole: a white frame on short legs, the cushion, the back
          // raised at the land end
          for ( let b = 0; b < beds; b++ ) {
            const bz = z + bedSide * ( 1.1 + b * 0.9 ), bx0 = x - 0.9, bx1 = x + 1.0;
            const legY = 0.32, backX = bx1 - 0.6;
            poly( [ P( bx0 + SUNX * 0.4, 0, bz - 0.3 + SUNZ * 0.4 ), P( bx1 + SUNX * 1.1, 0, bz - 0.3 + SUNZ * 1.1 ), P( bx1 + SUNX * 1.1, 0, bz + 0.3 + SUNZ * 1.1 ), P( bx0 + SUNX * 0.4, 0, bz + 0.3 + SUNZ * 0.4 ) ], this.rgba( inAir( [ 214, 196, 160 ], z ), 1 ) );
            if ( s > 2.5 ) {
              g.strokeStyle = 'rgba(235,235,230,0.95)';
              g.lineWidth = Math.max( 0.4, s * 0.04 );
              for ( const [ lxx, lzz ] of [ [ bx0, bz - 0.3 ], [ bx0, bz + 0.3 ], [ backX, bz - 0.3 ], [ backX, bz + 0.3 ] ] ) {
                const a = P( lxx, 0, lzz ), t2 = P( lxx, legY, lzz );
                g.beginPath(); g.moveTo( a[ 0 ], a[ 1 ] ); g.lineTo( t2[ 0 ], t2[ 1 ] ); g.stroke();
              }
            }
            // the seat and its cushion
            poly( [ P( bx0, legY, bz - 0.32 ), P( backX, legY, bz - 0.32 ), P( backX, legY, bz + 0.32 ), P( bx0, legY, bz + 0.32 ) ], this.rgba( inAir( [ 238, 236, 228 ], z ), 1 ) );
            poly( [ P( bx0 + 0.05, legY + 0.06, bz - 0.27 ), P( backX, legY + 0.06, bz - 0.27 ), P( backX, legY + 0.06, bz + 0.27 ), P( bx0 + 0.05, legY + 0.06, bz + 0.27 ) ], this.rgba( inAir( bedCol, z ), 1 ) );
            // the back, raised
            const bt = 0.75;
            poly( [ P( backX, legY + 0.06, bz - 0.27 ), P( backX + 0.55, legY + bt, bz - 0.27 ), P( backX + 0.55, legY + bt, bz + 0.27 ), P( backX, legY + 0.06, bz + 0.27 ) ], this.rgba( inAir( this.mix( bedCol, [ 0, 0, 0 ], 0.12 ), z ), 1 ) );
          }

          g.strokeStyle = 'rgba(110,110,110,0.9)';
          g.lineWidth = Math.max( 0.4, s * 0.05 );
          g.beginPath(); g.moveTo( bx, by ); g.lineTo( tx, ty ); g.stroke();
          const segs = 8, R = 1.25;
          const apex = tilt( 0, 2.62, 0 );
          const rim = ( q: number ) => { const a = q / segs * Math.PI * 2; return tilt( Math.cos( a ) * R, 2.25, Math.sin( a ) * R ); };
          const order = Array.from( { length: segs }, ( _, q ) => q ).sort( ( a, b ) => rim( b + 0.5 )[ 2 ] - rim( a + 0.5 )[ 2 ] );
          for ( const q of order ) {
            const r0 = rim( q ), r1 = rim( q + 1 );
            const facing = Math.cos( ( q + 0.5 ) / segs * Math.PI * 2 - Math.PI - 0.4 );
            const base = q % 2 ? this.mix( col, white, 0.8 ) : col;
            const shade = this.mix( base, facing > 0 ? white : [ 0, 0, 0 ], Math.abs( facing ) * ( facing > 0 ? 0.18 : 0.14 ) );
            poly( [ P( apex[ 0 ], apex[ 1 ], apex[ 2 ] ), P( r0[ 0 ], r0[ 1 ], r0[ 2 ] ), P( r1[ 0 ], r1[ 1 ], r1[ 2 ] ) ], this.rgba( inAir( shade, z ), 1 ) );
          }
          if ( s > 5 ) {
            const [ ax2, ay2 ] = P( apex[ 0 ], apex[ 1 ], apex[ 2 ] );
            g.fillStyle = 'rgba(240,240,236,1)';
            g.beginPath(); g.arc( ax2, ay2, Math.max( 0.6, s * 0.05 ), 0, Math.PI * 2 ); g.fill();
          }
        } } );
      }
      // a coconut seller's cart near us on the sand (Dock Town's Rio has one): a white box on two wheels
      // under a green and white striped umbrella, a pile of green coconuts on top
      {
        const zc = 34, xc = this.shoreX( zc ) + 48;
        items.push( { z: zc, draw: () => {
          const s = F / zc;
          poly( [ P( xc + SUNX * 0.9, 0, zc + SUNZ * 0.9 ), P( xc + 2 + SUNX * 0.9, 0, zc + SUNZ * 0.9 ), P( xc + 2 + SUNX * 0.9, 0, zc + 1 + SUNZ * 0.9 ), P( xc + SUNX * 0.9, 0, zc + 1 + SUNZ * 0.9 ) ], this.rgba( [ 214, 196, 160 ], 1 ) );
          // the box: its front and its top
          poly( [ P( xc, 0.4, zc ), P( xc + 2, 0.4, zc ), P( xc + 2, 1.1, zc ), P( xc, 1.1, zc ) ], 'rgb(244,244,238)' );
          poly( [ P( xc, 1.1, zc ), P( xc + 2, 1.1, zc ), P( xc + 2, 1.1, zc + 1 ), P( xc, 1.1, zc + 1 ) ], 'rgb(226,228,222)' );
          poly( [ P( xc, 0.7, zc - 0.01 ), P( xc + 2, 0.7, zc - 0.01 ), P( xc + 2, 0.85, zc - 0.01 ), P( xc, 0.85, zc - 0.01 ) ], 'rgb(19,161,102)' );
          // the wheels
          for ( const wx of [ xc + 0.35, xc + 1.65 ] ) {
            const [ cx3, cy3 ] = P( wx, 0.3, zc );
            g.fillStyle = 'rgb(50,50,52)';
            g.beginPath(); g.arc( cx3, cy3, s * 0.3, 0, Math.PI * 2 ); g.fill();
            g.fillStyle = 'rgb(200,200,196)';
            g.beginPath(); g.arc( cx3, cy3, s * 0.1, 0, Math.PI * 2 ); g.fill();
          }
          // the coconuts heaped on top
          const cc = this.seededLocal( 113 );
          for ( let q = 0; q < 26; q++ ) {
            const [ nx, ny ] = P( xc + 0.2 + cc() * 1.6, 1.2 + cc() * 0.25, zc + 0.15 + cc() * 0.7 );
            blob( nx, ny, s * 0.13, cc() < 0.5 ? [ 106, 160, 62 ] : [ 132, 182, 78 ], 1 );
          }
          // its umbrella on a pole
          const [ px2, py2 ] = P( xc + 1, 1.1, zc + 0.5 ), [ tx2, ty2 ] = P( xc + 1, 2.6, zc + 0.5 );
          g.strokeStyle = 'rgb(120,120,120)';
          g.lineWidth = Math.max( 0.6, s * 0.05 );
          g.beginPath(); g.moveTo( px2, py2 ); g.lineTo( tx2, ty2 ); g.stroke();
          for ( let q = 0; q < 8; q++ ) {
            const a0 = q / 8 * Math.PI * 2, a1 = ( q + 1 ) / 8 * Math.PI * 2;
            poly( [ P( xc + 1, 2.95, zc + 0.5 ), P( xc + 1 + Math.cos( a0 ) * 1.4, 2.6, zc + 0.5 + Math.sin( a0 ) * 1.4 ), P( xc + 1 + Math.cos( a1 ) * 1.4, 2.6, zc + 0.5 + Math.sin( a1 ) * 1.4 ) ], q % 2 ? 'rgb(248,248,244)' : 'rgb(19,161,102)' );
          }
        } } );
      }
      // a volleyball net, Copacabana's own: two posts and the net strung between them
      for ( const zn of [ netZ ] ) {
        const xn = netX;
        items.push( { z: zn, draw: () => {
          const s = F / zn;
          const a0 = P( xn, 0, zn ), a1 = P( xn, 2.45, zn ), b0 = P( xn + 9, 0, zn ), b1 = P( xn + 9, 2.45, zn ), n0 = P( xn, 1.7, zn ), n1 = P( xn + 9, 1.7, zn );
          g.strokeStyle = 'rgba(70,70,70,0.9)';
          g.lineWidth = Math.max( 0.5, s * 0.08 );
          g.beginPath(); g.moveTo( a0[ 0 ], a0[ 1 ] ); g.lineTo( a1[ 0 ], a1[ 1 ] ); g.moveTo( b0[ 0 ], b0[ 1 ] ); g.lineTo( b1[ 0 ], b1[ 1 ] ); g.stroke();
          g.fillStyle = 'rgba(40,40,40,0.18)';
          poly( [ a1, b1, n1, n0 ], 'rgba(40,40,40,0.18)' );
          g.strokeStyle = 'rgba(250,250,250,0.95)';
          g.lineWidth = Math.max( 0.5, s * 0.06 );
          g.beginPath(); g.moveTo( a1[ 0 ], a1[ 1 ] ); g.lineTo( b1[ 0 ], b1[ 1 ] ); g.stroke();
          if ( s > 4 ) {
            g.strokeStyle = 'rgba(40,40,40,0.35)';
            g.lineWidth = 0.5;
            for ( let q = 1; q < 18; q++ ) { const tq = q / 18; g.beginPath(); g.moveTo( a1[ 0 ] + ( b1[ 0 ] - a1[ 0 ] ) * tq, a1[ 1 ] ); g.lineTo( n0[ 0 ] + ( n1[ 0 ] - n0[ 0 ] ) * tq, n0[ 1 ] ); g.stroke(); }
          }
          // the lines of its court in the sand, faint
          g.strokeStyle = 'rgba(255,255,255,0.4)';
          g.lineWidth = Math.max( 0.4, s * 0.05 );
          const c0 = P( xn, 0, zn - 8 ), c1 = P( xn + 9, 0, zn - 8 ), c2 = P( xn + 9, 0, zn + 8 ), c3 = P( xn, 0, zn + 8 );
          g.beginPath(); g.moveTo( c0[ 0 ], c0[ 1 ] ); g.lineTo( c1[ 0 ], c1[ 1 ] ); g.lineTo( c2[ 0 ], c2[ 1 ] ); g.lineTo( c3[ 0 ], c3[ 1 ] ); g.closePath(); g.stroke();
        } } );
      }
      // the beach's kiosks along the promenade, white with a striped awning
      for ( let zk = 70; zk < 3000; zk += 210 ) {
        const xk = this.promX( zk ) - 6;
        items.push( { z: zk, draw: () => {
          const A = P( xk, 0, zk ), B = P( xk, 3, zk ), C = P( xk + 4, 3, zk ), D = P( xk + 4, 0, zk );
          poly( [ A, B, C, D ], this.rgba( inAir( [ 248, 248, 244 ], zk ), 1 ) );
          poly( [ P( xk - 0.6, 3.6, zk ), P( xk + 4.6, 3.6, zk ), P( xk + 4.6, 3, zk ), P( xk - 0.6, 3, zk ) ], this.rgba( inAir( [ 19, 161, 102 ], zk ), 1 ) );
          poly( [ P( xk + 0.3, 2.4, zk ), P( xk + 3.7, 2.4, zk ), P( xk + 3.7, 1.2, zk ), P( xk + 0.3, 1.2, zk ) ], this.rgba( inAir( [ 60, 72, 84 ], zk ), 0.8 ) );
        } } );
      }
      items.sort( ( a, b ) => b.z - a.z );
      for ( const it of items ) { it.draw(); }
      palmsTodo.sort( ( a, b ) => b.z - a.z );
      for ( const it of palmsTodo ) { it.draw(); }
      this.palms = pl.c;
    }
    this.back = c;

    // ---- the clouds: a few level banks, drifting slowly right ----
    const cl = canvas();
    const cr2 = this.seededLocal( 29 );
    // set out by the stage's height, so a wide screen gets more clouds, never wider ones (owner)
    const banks: [ number, number, number ][] = [];
    for ( let bx = cr2() * 0.3 * H; bx < W; bx += ( 0.75 + cr2() * 0.45 ) * H ) { banks.push( [ bx, 0.05 + cr2() * 0.11, ( 0.4 + cr2() * 0.25 ) * H ] ); }
    for ( const [ bx, v, bw ] of banks ) {
      const by = H * v;
      for ( let i = 0; i < 24; i++ ) {
        const a = cr2() * 2 - 1, heap = Math.sqrt( 1 - a * a );
        const px = bx + a * bw * 0.5, py = by - cr2() * heap * H * 0.025, pr = ( 0.4 + cr2() * 0.5 ) * H * 0.04 * ( 0.6 + heap );
        const tone = this.mix( [ 214, 228, 232 ], white, 0.6 + 0.4 * heap );
        for ( const dx of [ -W, 0, W ] ) {
          const pg = cl.g.createRadialGradient( px + dx, py - pr * 0.25, 0, px + dx, py, pr );
          pg.addColorStop( 0, this.rgba( tone, 0.6 ) );
          pg.addColorStop( 0.7, this.rgba( tone, 0.3 ) );
          pg.addColorStop( 1, this.rgba( tone, 0 ) );
          cl.g.fillStyle = pg;
          cl.g.fillRect( px + dx - pr, py - pr, pr * 2, pr * 2 );
        }
      }
    }
    this.clouds = cl.c;
  }

  /**
   * One palm on the promenade, in metres projected: a slender ringed trunk leaning a little, curving, and
   * its crown of drooping fronds with their leaflets, small and simple far off.
   */
  private palm( g: CanvasRenderingContext2D, P: ( x: number, y: number, z: number ) => [ number, number ], x: number, z: number, tall: number, lean: number, seed: number, col: RGB ): void {
    const s = this.F / z;
    const [ bx, by ] = P( x, 0, z );
    const [ tx, ty ] = P( x + lean, tall, z );
    const k = this.scaleK;
    if ( s < 1.2 ) {
      g.strokeStyle = this.rgba( this.mix( col, [ 120, 100, 80 ], 0.5 ), 0.9 );
      g.lineWidth = Math.max( 0.4, s * 0.3 );
      g.beginPath(); g.moveTo( bx, by ); g.lineTo( tx, ty ); g.stroke();
      g.fillStyle = this.rgba( col, 1 );
      g.beginPath(); g.arc( tx, ty, Math.max( 0.8, s * 2.4 ), 0, Math.PI * 2 ); g.fill();
      return;
    }
    // the trunk, thicker at its foot, a little curved, lit on its right
    const mx = ( bx + tx ) / 2 + s * lean * 0.4, my = ( by + ty ) / 2;
    const trunk: RGB = [ 150, 128, 100 ];
    for ( const [ w, c2 ] of [ [ 0.42, this.mix( trunk, [ 60, 50, 40 ], 0.3 ) ], [ 0.2, this.mix( trunk, [ 255, 240, 220 ], 0.25 ) ] ] as [ number, RGB ][] ) {
      g.strokeStyle = this.rgba( c2, 1 );
      g.lineCap = 'round';
      const steps = 10;
      for ( let i = 0; i < steps; i++ ) {
        const t0 = i / steps, t1 = ( i + 1 ) / steps;
        const at = ( t: number ): [ number, number ] => [ ( 1 - t ) * ( 1 - t ) * bx + 2 * ( 1 - t ) * t * mx + t * t * tx, ( 1 - t ) * ( 1 - t ) * by + 2 * ( 1 - t ) * t * my + t * t * ty ];
        const [ ax, ay ] = at( t0 ), [ cx2, cy2 ] = at( t1 );
        g.lineWidth = Math.max( 0.5, s * w * ( 1 - 0.4 * t0 ) * ( w > 0.3 ? 1 : 0.6 ) );
        g.beginPath(); g.moveTo( ax - ( w > 0.3 ? 0 : s * 0.08 ), ay ); g.lineTo( cx2 - ( w > 0.3 ? 0 : s * 0.08 ), cy2 ); g.stroke();
      }
    }
    // the crown: fronds arching out and drooping, leaflets along them
    const r = this.seededLocal( seed );
    const fronds = 11;
    const len = s * 4.2;
    for ( let i = 0; i < fronds; i++ ) {
      const a = -Math.PI / 2 + ( i / ( fronds - 1 ) - 0.5 ) * Math.PI * 1.9 + ( r() - 0.5 ) * 0.2;
      const droop = 0.5 + r() * 0.4;
      const fl = len * ( 0.8 + r() * 0.3 );
      const at = ( t: number ): [ number, number ] => [ tx + Math.cos( a ) * fl * t, ty + Math.sin( a ) * fl * t * 0.7 + fl * droop * t * t * 0.9 ];
      const tone = this.mix( col, i % 2 ? [ 0, 50, 26 ] : [ 200, 210, 120 ], 0.15 + r() * 0.15 );
      g.strokeStyle = this.rgba( tone, 1 );
      g.lineWidth = Math.max( 0.5, s * 0.12 );
      g.beginPath();
      for ( let q = 0; q <= 10; q++ ) { const [ px, py ] = at( q / 10 ); q ? g.lineTo( px, py ) : g.moveTo( px, py ); }
      g.stroke();
      if ( s > 3 ) {
        g.lineWidth = Math.max( 0.4, s * 0.06 );
        for ( let q = 1; q <= 14; q++ ) {
          const t = q / 15;
          const [ px, py ] = at( t ), [ nx, ny ] = at( Math.min( 1, t + 0.03 ) );
          const dir = Math.atan2( ny - py, nx - px );
          const ll = fl * 0.22 * Math.sin( Math.PI * t );
          for ( const sd of [ -1, 1 ] ) {
            const out = dir + sd * 1.0;
            g.beginPath(); g.moveTo( px, py ); g.quadraticCurveTo( px + Math.cos( out ) * ll * 0.6, py + Math.sin( out ) * ll * 0.6, px + Math.cos( out ) * ll * 0.8, py + Math.sin( out ) * ll * 0.8 + ll * 0.4 ); g.stroke();
          }
        }
      }
    }
    void k;
  }

  private seededLocal( seed: number ): () => number {
    let s = ( seed * 2654435761 ) >>> 0;
    return () => {
      s = ( s + 0x6D2B79F5 ) >>> 0;
      let t = Math.imul( s ^ ( s >>> 15 ), 1 | s );
      t = ( t + Math.imul( t ^ ( t >>> 7 ), 61 | t ) ) ^ t;
      return ( ( t ^ ( t >>> 14 ) ) >>> 0 ) / 4294967296;
    };
  }

  /** The colour circle's five, or Rio's own: mint, sage, rainforest green, coastal blue, sun yellow. */
  private five(): RGB[] {
    if ( this.paletteColors.length >= 5 ) { return this.paletteColors.slice( 0, 5 ); }
    return [ [ 217, 239, 210 ], [ 133, 183, 120 ], [ 19, 161, 102 ], [ 34, 135, 213 ], [ 236, 218, 97 ] ];
  }
}
